import { inject, Injectable } from "@angular/core";
import { ValueOf } from "@cureskin/api-client/src/common";
import { ApiClientConstant } from "@cureskin/api-client/src/constant/api-client.constant";
import { User } from "@cureskin/api-client/src/server-api";
import { ConnectionService } from "@services/connection-service";

export type ActivePregnancyTag =
  | typeof ApiClientConstant.User.MarketingTags.PREGNANT
  | typeof ApiClientConstant.User.MarketingTags.NEW_MOM
  | typeof ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY;

const BeforeOrderPlacedStages = [
  ApiClientConstant.User.OrderState.NEW_USER,
  ApiClientConstant.User.OrderState.UNPAID,
];

const UnpaidUserOrderStates = [
    ...BeforeOrderPlacedStages,
  ApiClientConstant.User.OrderState.PROCESSING,
];

@Injectable({providedIn: 'root'})
export class UserService {
  private readonly connectionService = inject(ConnectionService);

  readonly devnagriScripts: ReadonlyArray<string> = [
    ApiClientConstant.LanguageString.HI,
    ApiClientConstant.LanguageString.MR,
  ];

  readonly dravidianScripts: ReadonlyArray<string> = [
    ApiClientConstant.LanguageString.KN,
    ApiClientConstant.LanguageString.TE,
  ];

  get isDravidianLanguage(): boolean {
    return this.dravidianScripts.includes(this.getUserLanguagePreference());
  }

  getUser(): User {
    return this.connectionService.getCurrentUser();
  }

  getUserLanguagePreference(): string {
    return this.connectionService.getCurrentUser()?.get('languagePreference') ?? ApiClientConstant.LanguageString.EN;
  }

  getUserGender(): string {
    return this.connectionService.getCurrentUser()?.get('Gender') ?? ApiClientConstant.User.Gender.FEMALE;
  }

  hasSelectedMainConcern(): boolean {
    const user = this.getUser();
    if (!user) return false;
    if (user.get('previousRegimenConcern')) return true;
    return !!user.get('PrivateMainConcern') && !user.get('mainConcernMandatory');
  }

  isPaidUser(user: User | Table.User): boolean {
    return !this.isUnpaidUser(user);
  }

  isUnpaidUser(user: User | Table.User): boolean {
    return UnpaidUserOrderStates.includes(user.get('orderState'));
  }

  hasNotPlacedOrder(user: User | Table.User): boolean {
    return BeforeOrderPlacedStages.includes(user.get('orderState'));
  }

  isNewOrUnpaidUser(user: User | Table.User): boolean {
    const orderState = user.get('orderState');
    return orderState === ApiClientConstant.User.OrderState.NEW_USER
      || orderState === ApiClientConstant.User.OrderState.UNPAID;
  }

  // Retention user with an active regimen: delivered order AND ON_REGIMEN marketing tag.
  // Note: retention users without a regimen are intentionally excluded.
  isRetentionUserOnRegimen(user: User | Table.User): boolean {
    return user?.get('orderState') === ApiClientConstant.User.OrderState.DELIVERED
      && !!user?.get('marketingTags')?.includes(ApiClientConstant.User.MarketingTags.ON_REGIMEN);
  }

  hasPregnancyRelatedTags(marketingTags: Array<ValueOf<typeof ApiClientConstant.User.MarketingTags>>): boolean {
    return marketingTags.includes(ApiClientConstant.User.MarketingTags.PREGNANT) ||
      marketingTags.includes(ApiClientConstant.User.MarketingTags.NEW_MOM) ||
      marketingTags.includes(ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY);
  }

  getActivePregnancyTag(marketingTags: Array<ValueOf<typeof ApiClientConstant.User.MarketingTags>>): ActivePregnancyTag | null {
    if (marketingTags.includes(ApiClientConstant.User.MarketingTags.NEW_MOM)) return ApiClientConstant.User.MarketingTags.NEW_MOM;
    if (marketingTags.includes(ApiClientConstant.User.MarketingTags.PREGNANT)) return ApiClientConstant.User.MarketingTags.PREGNANT;
    if (marketingTags.includes(ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY)) return ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY;
    return null;
  }
}
